import { useEffect, useRef, useState } from 'react';
import { API } from '@/lib/api';
import { AI } from '@/constants/testIds';
import { useAuth } from '@/lib/auth';
import ProUpsell from '@/components/ProUpsell';
import Seo from '@/components/Seo';
import { Send, Bot, User as UserIcon, Sparkles } from 'lucide-react';

const STARTERS = [
  'Estimate a 4×3m bathroom refit',
  'How many bricks for a 12m × 2.4m single-skin wall?',
  'BTU for a 4×4m living room',
  'Voltage drop for 32A cooker circuit, 18m run',
];

export default function AssistantPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Alright mate — I'm TradesMate AI. Ask me to estimate any UK job (materials, labour, VAT, margin) or ping me a quick calc. Try one of the suggestions below 👇" }
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const logRef = useRef(null);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: msg }, { role: 'assistant', content: '' }]);
    setBusy(true);

    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      if (resp.status === 401) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: 'assistant', content: 'Please log in to use the AI assistant.' };
          return copy;
        });
        return;
      }
      if (!resp.body) throw new Error('No stream body');
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          const line = part.split('\n').find((l) => l.startsWith('data: '));
          const evLine = part.split('\n').find((l) => l.startsWith('event: '));
          if (evLine?.includes('done')) {
            const sidLine = part.split('\n').find((l) => l.startsWith('data: '));
            if (sidLine) setSessionId(sidLine.slice(6).trim());
            continue;
          }
          if (line) {
            const chunk = line.slice(6).replaceAll('<NL>', '\n');
            setMessages((m) => {
              const copy = [...m];
              const last = { ...copy[copy.length - 1] };
              last.content += chunk;
              copy[copy.length - 1] = last;
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: 'assistant', content: 'Connection error. Try again.' };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo title="AI Estimator" noindex />
      {!user?.is_pro_plus && user?.role !== 'admin' ? (
        <ProUpsell feature="AI Assistant" testIdSuffix="assistant" tier="pro_plus"/>
      ) : (
      <>
      <header className="mb-6">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">AI Assistant</p>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1 flex items-center gap-3">
          <Sparkles className="text-brand" size={36}/> TradesMate AI
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Trained for UK trades. Ask in plain English — get materials, labour, VAT and margin breakdowns.</p>
      </header>

      <div className="tm-card p-0 overflow-hidden">
        <div ref={logRef} data-testid={AI.log} className="h-[55vh] overflow-y-auto p-5 sm:p-6 space-y-4 bg-[#FAFAF5]">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'assistant' && (
                <div className="shrink-0 w-9 h-9 border border-slate-200 bg-brand text-white flex items-center justify-center">
                  <Bot size={18}/>
                </div>
              )}
              <div className={`max-w-[80%] border border-slate-200 p-3 sm:p-4 font-mono text-sm whitespace-pre-wrap ${
                m.role === 'user' ? 'bg-slate-900 text-white' : 'bg-white'
              }`}>
                {m.content || (m.role === 'assistant' ? <span className="text-slate-400">…thinking</span> : '')}
              </div>
              {m.role === 'user' && (
                <div className="shrink-0 w-9 h-9 border border-slate-200 bg-white text-slate-900 flex items-center justify-center">
                  <UserIcon size={18}/>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="border-t border-slate-200 p-4 bg-white">
          <div className="flex flex-wrap gap-2 mb-3">
            {STARTERS.map((s, i) => (
              <button key={i} onClick={() => send(s)} data-testid={`ai-starter-${i}`}
                className="tm-chip hover:bg-slate-900 hover:text-white transition-colors">
                {s}
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-end">
            <textarea
              data-testid={AI.input}
              value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder="Estimate a 4×3 bathroom refit…"
              className="tm-input flex-1 resize-none"
            />
            <button data-testid={AI.send} onClick={() => send()} disabled={busy || !input.trim()} className="tm-btn">
              <Send size={16}/> {busy ? '…' : 'Send'}
            </button>
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
